// Mirrored from mju-aioe/packages/shared/src/schemas/projectMap.ts (main af5e2bf) — keep the two identical.
/**
 * The project map (roadmap U2): a project's plans, architecture and roadmap, drawn in the
 * console from what the workbench mirrors here and from the shared boards and memory.
 *
 *   PUT /projects/v1/:id/roots/:rootId/map    a workbench (its member's device token, or a contributor signed in) mirrors
 *                                             what it knows for one repository: the architecture model's layers and areas,
 *                                             the rows of docs/roadmap/README.md, and every plan with its nodes; the whole
 *                                             map for that repository is replaced, `revision` says which state it is
 *   GET /projects/v1/:id/map                  members: every repository's mirrored map, the shared boards' tasks with their
 *                                             dependencies, and the memory graph's communities with the links between them
 *
 * Mirrors the workbench's `src/coordination/plan.ts` (Plan, PlanNode), `src/memory/graphModel.ts`
 * (ArchLayer, ArchArea) and `src/docs/roadmapSync.ts` (RoadmapRow) in the fields the console draws;
 * a newer workbench's extra fields on a plan or an area survive the round trip.
 *
 * Words for people: roadmap, plan, architecture, area, layer, task; never DAG or graph in a label.
 *
 * AIOE 0.35.0 (the workbench's D247; optional fields, no version bump): a roadmap row may carry `owner` (a name, or `{ id?, name }`), a plan
 * `queuedAfter` (waiting for another team's plan, plans.ts) and `rowAlongside` (worked alongside another's, by a person's choice).
 */
import { z } from "zod";
import { taskClosureKindSchema, taskDelegateSchema, taskParentSchema, taskRollupSchema } from "./projectLinks.js";
import { roadmapRefSchema } from "./projectRuns.js";
export const MAP_SCHEMA_VERSION = 1;
const id = z.string().min(1).max(200);
export const archLayerSchema = z.looseObject({ id, title: z.string().max(200), description: z.string().max(2000).optional() });
export const archAreaSchema = z.looseObject({
    id,
    path: z.string().max(500),
    title: z.string().max(200),
    description: z.string().max(2000).optional(),
    layer: id,
    kind: z.string().max(50).optional(),
    files: z.number().int().nonnegative().optional(),
    symbols: z.number().int().nonnegative().optional(),
    dependsOn: z.array(z.object({ id, weight: z.number().nonnegative().default(1) })).max(500).default([]),
});
export const architectureSchema = z.looseObject({
    title: z.string().max(200).optional(),
    summary: z.string().max(4000).optional(),
    layers: z.array(archLayerSchema).max(50),
    areas: z.array(archAreaSchema).max(500),
});
/**
 * An owner (AIOE 0.35.0, the workbench's D247, B-12): a person on the project, by their id at the home when known and
 * always by name. Optional everywhere it appears; no schema version moves.
 */
export const ownerRefSchema = z.looseObject({ id: z.string().min(1).max(200).optional(), name: z.string().min(1).max(200) });
/** An owner as a repository roadmap mirrors it: the workbench's Owner column is a name; a project row keeps `{ id?, name }`. */
export const roadmapOwnerSchema = z.union([z.string().min(1).max(200), ownerRefSchema]);
/** The owner's name, whichever form it came in. */
export const ownerNameOf = (owner) => (typeof owner === "string" ? owner.trim() || undefined : owner?.name?.trim() || undefined);
/**
 * Another team's plan, as a row hold names it (D247): by its team and plan, with the plan's PLAN-n and who chose. The workbench's
 * `PlanRowHold`; `rootId` (the other plan's repository, "" for the project's own) is the platform's addition.
 */
export const planRowHoldSchema = z.looseObject({
    teamId: z.string().max(200).optional(),
    planId: z.string().min(1).max(200),
    planRef: z.string().max(50).optional(),
    rootId: z.string().max(200).optional(),
    at: z.string().max(50).optional(),
    /** The person who chose, by name. */
    by: z.string().max(200).optional(),
});
/** A plan worked alongside another on a roadmap row, by a person's choice (D247, C-8): the hold and the row. */
export const planRowAlongsideSchema = planRowHoldSchema.extend({ row: z.string().min(1).max(200) });
export const roadmapStatusSchema = z.enum(["not-started", "in-progress", "done", "deferred", "wont-do"]);
export const roadmapRowSchema = z.looseObject({
    id,
    title: z.string().max(500),
    area: z.string().max(500),
    phase: z.string().max(50),
    status: roadmapStatusSchema,
    evidence: z.string().max(4000).default(""),
    dependsOn: z.array(id).max(100).optional(),
    /** Who is accountable for the row (AIOE 0.35.0, D247, B-12): the workbench's Owner column (a name). */
    owner: roadmapOwnerSchema.optional(),
});
export const planStatusSchema = z.enum(["draft", "approved", "dispatched", "done", "verified", "abandoned"]);
export const planNodeSchema = z.looseObject({
    ref: id,
    taskId: z.string().max(100).optional(),
    title: z.string().max(1000),
    assignee: z.string().max(200).optional(),
    role: z.string().max(200).optional(),
    dependsOn: z.array(id).max(200).default([]),
    files: z.array(z.string().max(500)).max(200).optional(),
    /** Acceptance criteria ids this node satisfies. */
    done: z.array(id).max(100).optional(),
    /** A part done in another repository of the project: that repository, and the task there that carries it. */
    rootId: id.optional(),
    task: taskDelegateSchema.optional(),
});
export const planSchema = z.looseObject({
    id,
    ref: z.string().max(50).optional(),
    teamId: id,
    teamName: z.string().max(200).optional(),
    objective: z.string().max(4000),
    summary: z.string().max(8000).optional(),
    status: planStatusSchema,
    phase: z.number().int().optional(),
    briefId: z.string().max(200).optional(),
    author: z.string().max(200).default("user"),
    createdAt: z.string(),
    updatedAt: z.string(),
    nodes: z.array(planNodeSchema).max(500).default([]),
    acceptance: z.array(z.looseObject({ id, text: z.string().max(2000), passed: z.boolean().optional() })).max(200).default([]),
    /** Node refs on the critical path, in order. */
    criticalPath: z.array(id).max(500).default([]),
    progress: z.object({ planned: z.number().int().nonnegative(), done: z.number().int().nonnegative(), failed: z.number().int().nonnegative().default(0), skipped: z.number().int().nonnegative().default(0) }).optional(),
    /** The project task the plan rolls up into, when it has parts in other repositories of the project. */
    projectTask: z.object({ id, uid: id }).optional(),
    /** The roadmap items the plan serves: this repository's rows, or the project's (`rootId: ""`). */
    roadmap: z.array(roadmapRefSchema).max(20).optional(),
    /** Waiting for another team's plan that serves one of its roadmap rows (AIOE 0.35.0, D247): a draft until that plan finishes. */
    queuedAfter: planRowHoldSchema.optional(),
    /** Worked alongside another team's plan on these rows, by a person's choice (AIOE 0.35.0, D247). */
    rowAlongside: z.array(planRowAlongsideSchema).max(50).optional(),
});
/** A board task as the map carries it: enough to draw it, to join it to a plan node and to roll it up. */
export const mapTaskSchema = z.object({
    id: z.string().min(1).max(100),
    uid: z.string().max(200).optional(),
    title: z.string().max(1000),
    status: z.string().max(50),
    priority: z.string().max(50),
    assignee: z.string().max(200).optional(),
    dependencies: z.array(z.string().max(100)).max(500).default([]),
    labels: z.array(z.string().max(200)).max(100).default([]),
    updatedAt: z.string().max(50).optional(),
    /** How the task was closed, when it was (AIOE 0.34.0, done means done): `done`, `cancelled`, `refused`, `handed-on` or `blocked`. A cancelled task has status `blocked` and kind `cancelled`. */
    closure: z.object({ kind: taskClosureKindSchema }).optional(),
    /** A project task: the repository tasks that carry it, and what they add up to. */
    delegates: z.array(taskDelegateSchema).optional(),
    rollup: taskRollupSchema.optional(),
    /** A repository task: the project task it carries part of. */
    parent: taskParentSchema.optional(),
    /** The roadmap items the task serves: its repository's rows, or the project's (`rootId: ""`). */
    roadmap: z.array(roadmapRefSchema).max(20).optional(),
});
/** What a workbench mirrors for one repository. Anything left out is unknown, not empty. */
export const projectMapPutSchema = z.object({
    schemaVersion: z.literal(MAP_SCHEMA_VERSION),
    /** The workbench's own mark of the state (a commit, a hash, a time). */
    revision: z.string().min(1).max(200),
    architecture: architectureSchema.optional(),
    roadmap: z.array(roadmapRowSchema).max(2000).optional(),
    plans: z.array(planSchema).max(500).optional(),
    /**
     * The repository's own boards, sent only where the project's home does not
     * keep them (a project on the private hub: its boards are the repository's files).
     * A home that keeps the boards ignores this.
     */
    boards: z.array(z.object({ board: id, tasks: z.array(mapTaskSchema).max(5000) })).max(200).optional(),
});
export const ROADMAP_STATUS_TEXT = { "not-started": "Not started", "in-progress": "In progress", done: "Done", deferred: "Deferred", "wont-do": "Won't do" };
export const PLAN_STATUS_TEXT = { draft: "Draft", approved: "Approved", dispatched: "Under way", done: "Done", verified: "Verified", abandoned: "Abandoned" };
//# sourceMappingURL=projectMap.js.map